import React from 'react';
import { BuildingIcon, LinkIcon, UserEditIcon } from '@newcore/icons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Text } from '@newcore/ui/typography';
import newcoreAppIcon from '@/assets/newcore-app-icon.svg';
import { Onboarding } from '@/features/onboarding/components/primitives';

interface TenantWelcomeStepProps {
  userName?: string;
  companyName: string;
  tenantUrl?: string;
  onGetStarted: () => void;
  isLoading?: boolean;
}

/**
 * Tenant Welcome Step - First step of the tenant onboarding wizard
 *
 * Shows organization details and explains what the user will be able to do
 * as the first Global Administrator.
 */
export const TenantWelcomeStep: React.FC<TenantWelcomeStepProps> = ({
  userName,
  companyName,
  tenantUrl,
  onGetStarted,
  isLoading = false,
}) => {
  const displayName = userName ?? 'there';

  return (
    <div className='flex flex-col items-center text-center space-y-6 py-2'>
      {/* Logo */}
      <img
        src={newcoreAppIcon}
        alt='NewCore'
        className='h-20 w-20 object-contain'
      />

      {/* Welcome Message */}
      <Onboarding.Header>
        <Onboarding.Title>Welcome, {displayName}!</Onboarding.Title>
        <Onboarding.Description>
          You&apos;re about to activate your organization&apos;s NewCore tenant.
        </Onboarding.Description>
      </Onboarding.Header>

      {/* Organization Details Card */}
      <div className='w-full rounded-lg border bg-primary p-4 text-left space-y-3'>
        {/* Organization Name */}
        <div className='flex items-start gap-3'>
          <BuildingIcon className='h-5 w-5 text-tertiary mt-0.5 flex-shrink-0' />
          <div>
            <Text as='p' className='text-tertiary'>
              Organization
            </Text>
            <Text as='p' weight={FontWeights.MEDIUM}>
              {companyName}
            </Text>
          </div>
        </div>

        {/* Tenant URL */}
        {tenantUrl && (
          <div className='flex items-start gap-3'>
            <LinkIcon className='h-5 w-5 text-tertiary mt-0.5 flex-shrink-0' />
            <div>
              <Text as='p' className='text-tertiary'>
                Tenant URL
              </Text>
              <Text as='p' weight={FontWeights.MEDIUM}>
                {tenantUrl}
              </Text>
            </div>
          </div>
        )}

        {/* Role */}
        <div className='flex items-start gap-3'>
          <UserEditIcon className='h-5 w-5 text-tertiary mt-0.5 flex-shrink-0' />
          <div>
            <Text as='p' className='text-tertiary'>
              Your Role
            </Text>
            <Text as='p' weight={FontWeights.MEDIUM}>
              Global Administrator
            </Text>
          </div>
        </div>
      </div>

      {/* What you'll be able to do */}
      <div className='w-full rounded-lg border-l-4 border-l-primary bg-secondary/50 p-4 text-left'>
        <Text as='p' weight={FontWeights.MEDIUM} className='mb-2'>
          As the first Global Admin, you&apos;ll:
        </Text>
        <Text as='ul' className='space-y-1.5 text-tertiary'>
          <li className='flex items-start gap-2'>
            <span className='text-primary'>•</span>
            Set up secure authentication for your account
          </li>
          <li className='flex items-start gap-2'>
            <span className='text-primary'>•</span>
            Get access to the Admin Console
          </li>
          <li className='flex items-start gap-2'>
            <span className='text-primary'>•</span>
            Be able to invite your team and configure security
          </li>
        </Text>
      </div>

      {/* Get Started Button */}
      <Onboarding.NextButton
        onClick={onGetStarted}
        loading={isLoading}
        className='w-full max-w-xs mt-2'
      >
        Get Started
      </Onboarding.NextButton>
    </div>
  );
};
